import { useEffect, useState } from "react";
import apiAgent from "../../api/apiAgent";
import QRCode from "react-qr-code";
import { Loader } from "../Loader";
import { Button } from "../Button";
import PinField from "react-pin-field";
import { MFAContainer, VerifyPinField } from "./utils";
import { tMsg } from "../ToastNotifications";
import _ from "lodash";
import { pause } from "../../utils";
import { useNavigate } from "react-router";
import { useSetAtom } from "jotai";
import { loginStatusAtom } from "../../atoms";

const IntroPage = ({ nextPage }: { nextPage: () => void }) => (
  <MFAContainer className="text-center">
    <div className="text-lg font-bold">Multi-Factor Authentication</div>
    <div className="text-sm space-y-2">
      <p>
        Please register a device for multi-factor authentication (MFA) to
        continue.
      </p>
      <p>
        You will require an authentication app such as 'Google Authenticator' to
        be installed on your device.
      </p>
    </div>
    <Button onClick={() => nextPage()}>Begin Activation</Button>
  </MFAContainer>
);

const SuccessPage = () => {
  const navigate = useNavigate();
  const resetToken = async () => {
    try {
      const response = await apiAgent.Auth.deregisterMFA();
      if (response.status < 299) {
        tMsg.success("2FA Token has been successfully reset.");
        await pause(2000);
        navigate(0);
      }
    } catch (err) {
      console.log(err);
      tMsg.error("Failed to reset token");
    }
  };
  return (
    <MFAContainer className="py-8 space-y-2">
      <div className="w-full text-center font-bold">Activation Successful</div>
      <div className="text-sm text-center space-y-1">
        <p>Your token has been verified.</p>
        <p>
          When you next login, you will be required to enter a 6 digit code from
          your authenticator app.
        </p>
      </div>
      <div className="flex justify-center w-full">
        <a href="/">
          <Button>Continue</Button>
        </a>
      </div>
    </MFAContainer>
  );
};

const VerifyPage = ({
  qrUri,
  fetchQRCode,
  onComplete,
  verifying,
  error,
}: {
  qrUri: string | null;
  fetchQRCode: () => void;
  onComplete: (e: string) => void;
  verifying: boolean;
  error?: string;
}) => {
  const [showTextCode, setShowTextCode] = useState<boolean>(false);
  const getKey = qrUri ? new URL(qrUri).searchParams.get("secret") : "";
  return qrUri !== null ? (
    <MFAContainer>
      <div className="text-center w-full pt-4 text">
        {!showTextCode
          ? `Scan the QR Code using an authenticator app on your mobile device.`
          : `Manually enter the code shown below to your authenticator app`}
      </div>
      <div className="w-full flex justify-center font-bold text-sm">
        <button
          onClick={(e) => {
            e.preventDefault();
            setShowTextCode(!showTextCode);
          }}
          className="transition opacity-70 hover:opacity-100"
        >
          {showTextCode ? `Use QR scanner` : `Use manual code entry`}
        </button>
      </div>
      <div className="w-full grid place-content-center h-[220px] bg-gray-500/10">
        {verifying ? (
          <Loader />
        ) : (
          <>
            {!showTextCode ? (
              <div className="w-full flex justify-center">
                <QRCode size={180} value={qrUri} />
              </div>
            ) : (
              <div className="text-lg font-bold px-4 py-2 bg-black/20 rounded">
                {getKey}
              </div>
            )}
          </>
        )}
      </div>
      <VerifyPinField onComplete={onComplete} validating={verifying} />
      <div className="h-4 w-full text-center pb-9 text-sm font-bold text-red-600">
        {error}
      </div>
    </MFAContainer>
  ) : (
    <MFAContainer>
      <div className="text-center w-full h-[200px] grid content-center px-8">
        An error occured when generating your token, please try again.
      </div>
      <Button onClick={() => fetchQRCode()}>Try again</Button>
    </MFAContainer>
  );
};

const registerPhases = ["intro", "activate", "success"];
type RegisterPhase = (typeof registerPhases)[number];

export const RegisterPage = () => {
  const [fetching, setFetching] = useState<boolean>(false);
  const [verifying, setVerifying] = useState<boolean>(false);
  const [qrUri, setQrUri] = useState<string | null>(null);
  const [phase, setPhase] = useState<RegisterPhase>("intro");
  const [verifyError, setVerifyError] = useState<string>("");
  const setLoginStatus = useSetAtom(loginStatusAtom);

  const fetchQRCode = async () => {
    try {
      setFetching(true);
      const response = await apiAgent.Auth.registerMFA();
      if (response.status === 200) {
        setQrUri(response.data.uri);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setFetching(false);
    }
  };

  const activateCode = async (code: string) => {
    try {
      setVerifying(true);
      setVerifyError("");
      const response = await apiAgent.Auth.verifyMFA(_.toNumber(code), true);
      await pause(500);
      if (response.status === 202 || response.status === 200) {
        tMsg.success("2FA Successfully activated");
        setPhase("success");
      }
    } catch (err: any) {
      console.log(err);
      if (err.response.status === 401) {
        setVerifyError("Invalid code");
      }
      if (err.response.status === 403) {
        setVerifyError("Code expired, please try again");
      }
    } finally {
      setVerifying(false);
    }
  };

  if (fetching) return <Loader />;

  return phase === "intro" ? (
    <IntroPage
      nextPage={() => {
        fetchQRCode();
        setPhase("activate");
      }}
    />
  ) : phase === "activate" ? (
    <VerifyPage
      qrUri={qrUri}
      fetchQRCode={fetchQRCode}
      onComplete={(e) => activateCode(e)}
      verifying={verifying}
      error={verifyError}
    />
  ) : (
    <SuccessPage />
  );
};
